/**
 * L'état d'un poste d'extras, calculé à UN SEUL endroit.
 *
 * Le process d'Emeni : le besoin est saisi sur la privatisation, les collaborateurs bookent d'abord les
 * extras INTERNES, et seul le reliquat part chez le prestataire — « sur 4 hôtesses j'en ai booké 2 en
 * interne, du coup 2 partent en à demander » (2026-08-28). C'est arithmétique : personne n'a à déclencher
 * la bascule, et un désistement fait remonter le manque tout seul.
 *
 * Cette fonction est volontairement pure et sans rendu : elle est consommée par la fiche de privatisation
 * ET par le suivi global des extras. Le suivi de privatisation existe déjà en deux exemplaires qui ont
 * divergé, et le résumé des extras en deux copies côté serveur — dupliquer cette règle-ci reviendrait à
 * garantir que les deux écrans finissent par se contredire.
 */

export interface CompteursPoste {
    /** Ce que la privatisation exige pour ce poste. */
    requis: number;
    /** Salariés de la maison déjà bookés dessus. */
    interne: number;
    /** Quantité commandée au prestataire (envoyée ou non). */
    commande: number;
    /** Part de cette commande réellement PARTIE chez le prestataire. Créer une demande depuis « Booker »
     *  ne l'envoie pas : sans ce compteur, une commande jamais envoyée s'afficherait « réponse attendue »
     *  alors que rien n'a quitté la maison. */
    envoye: number;
    /** Part de cette commande que le prestataire a confirmée. */
    confirme: number;
    /** Au moins un salarié sait tenir ce poste. Un agent de sécurité n'est jamais dans ce cas. */
    tenableEnInterne: boolean;
}

export type EtatPoste = 'a_booker_interne' | 'a_commander' | 'reponse_attendue' | 'complet';

export interface EtatCalcule {
    etat: EtatPoste;
    /** Ce qui n'est couvert ni en interne ni par une commande — le nombre à faire partir chez le prestataire. */
    reste: number;
}

/** Intitulés choisis avec Emeni : chacun dit QUI doit agir, c'est ce qu'on vient chercher sur un suivi. */
export const LIBELLE_ETAT: Record<EtatPoste, string> = {
    a_booker_interne: 'à booker en interne',
    a_commander: 'à commander',
    reponse_attendue: 'réponse attendue',
    complet: 'complet',
};

/** Du moins avancé au plus avancé — sert à donner son état à un dossier entier. */
export const ORDRE_ETATS: EtatPoste[] = ['a_booker_interne', 'a_commander', 'reponse_attendue', 'complet'];

export const etatDuPoste = (c: CompteursPoste): EtatCalcule => {
    const requis = Math.max(0, c.requis || 0);
    const interne = Math.max(0, c.interne || 0);
    const commande = Math.max(0, c.commande || 0);
    const envoye = Math.max(0, c.envoye || 0);
    const confirme = Math.max(0, c.confirme || 0);
    const reste = Math.max(0, requis - interne - commande);
    // Ce qui est commandé sans être parti reste à la charge de la maison, au même titre qu'un poste pas
    // encore commandé : dans les deux cas, la balle est ici. C'est le critère qui a présidé au choix des
    // quatre intitulés — chacun dit qui doit agir.
    const aEnvoyer = Math.max(0, commande - envoye);

    // « Complet » plutôt que « confirmé » : un poste peut être entièrement pourvu par des salariés de la
    // maison, sans qu'aucun prestataire ne soit intervenu. Dire « confirmé » laisserait croire le contraire.
    if (reste === 0 && aEnvoyer === 0 && commande === confirme) return { etat: 'complet', reste: 0 };

    // Une commande partie chez le prestataire attend sa réponse, même s'il reste des postes à pourvoir :
    // c'est lui qu'on attend, pas nous.
    if (reste === 0 && aEnvoyer === 0) return { etat: 'reponse_attendue', reste: 0 };

    // Il manque du monde. Tant que personne n'a booké en interne ET que la maison sait tenir ce poste,
    // c'est aux collaborateurs de jouer. Dès qu'un premier booking interne existe, le reliquat est pour le
    // prestataire — c'est exactement la règle qu'Emeni a décrite.
    if (reste > 0 && interne === 0 && c.tenableEnInterne) return { etat: 'a_booker_interne', reste };

    return { etat: 'a_commander', reste };
};

/** L'état d'un dossier : celui de son poste le moins avancé, car c'est ce qui reste à faire qui compte. */
export const etatLeMoinsAvance = (etats: EtatPoste[]): EtatPoste =>
    [...etats].sort((a, b) => ORDRE_ETATS.indexOf(a) - ORDRE_ETATS.indexOf(b))[0] || 'complet';
